import type {
  ChatTrigger,
  ChatTriggerActionsItem,
  ChatTriggerCondition,
  CreateChatTriggerRequest,
  CreateChatTriggerRequestActionsItem,
  UpdateChatTriggerRequest,
} from '@nucleus-commerce/api-client/admin';
import { fromMajor, toMajorString } from '@nucleus-commerce/core';

import {
  isConditionField,
  OPS,
  VALUE_KIND,
  type ActionForm,
  type ActionType,
  type ConditionField,
  type ConditionForm,
  type TriggerForm,
} from '../schemas/trigger-schema';

export function emptyCondition(field: ConditionField = 'url'): ConditionForm {
  const kind = VALUE_KIND[field];
  return {
    field,
    op: OPS[kind][0] ?? 'in',
    value: kind === 'flag' ? 'true' : '',
    values: [],
    key: '',
  };
}

export function emptyAction(type: ActionType = 'message'): ActionForm {
  return {
    type,
    body: '',
    sendAs: 'store',
    alias: '',
    asUserId: '',
    suggestedReplies: [],
    seconds: type === 'wait' ? '3' : '',
    tag: '',
    productId: '',
    productTitle: '',
    discountId: '',
  };
}

export function emptyTrigger(position: number): TriggerForm {
  return {
    name: '',
    kind: 'site',
    enabled: true,
    oncePerVisitor: false,
    rescueAfterSeconds: '',
    position: String(position),
    // nobody to answer a greeting outside hours unless the store says otherwise
    conditions: [{ ...emptyCondition('agentsOnline'), value: 'true' }],
    actions: [emptyAction('message')],
  };
}

function conditionToForm(condition: ChatTriggerCondition, currency: string): ConditionForm {
  const base = { field: condition.field, op: condition.op, value: '', values: [], key: '' };
  switch (condition.field) {
    case 'url':
    case 'referrer':
      return { ...base, value: condition.value };
    case 'cartTotalMinor':
      return { ...base, value: toMajorString({ amount: condition.value, currency }) };
    case 'timeOnPageSeconds':
    case 'timeOnSiteSeconds':
    case 'pageViews':
    case 'visits':
    case 'previousChats':
    case 'hourOfDay':
      return { ...base, value: String(condition.value) };
    case 'agentsOnline':
    case 'isCustomer':
    case 'hasOrderInProduction':
    case 'exitIntent':
      return { ...base, value: condition.value ? 'true' : 'false' };
    case 'viewingProductAttribute':
      return { ...base, key: condition.key, value: String(condition.value) };
    case 'cartHasProductTag':
    case 'cartHasProductType':
    case 'viewingProductTag':
    case 'checkoutStep':
    case 'region':
    case 'city':
    case 'dayOfWeek':
    case 'device':
    case 'country':
      return { ...base, values: [...condition.value] };
  }
}

function actionToForm(
  action: ChatTriggerActionsItem,
  productTitles: ReadonlyMap<string, string>,
): ActionForm {
  const form = emptyAction(action.type);
  switch (action.type) {
    case 'message':
      return {
        ...form,
        body: action.body,
        sendAs: action.asUserId ? 'member' : action.alias ? 'alias' : 'store',
        alias: action.alias ?? '',
        asUserId: action.asUserId ?? '',
        suggestedReplies: [...action.suggestedReplies],
      };
    case 'wait':
      return { ...form, seconds: String(action.seconds) };
    case 'tag':
      return { ...form, tag: action.tag };
    case 'product_card':
      return {
        ...form,
        productId: action.productId,
        productTitle: productTitles.get(action.productId) ?? '',
      };
    case 'discount':
      return { ...form, discountId: action.discountId };
  }
}

export function fromTrigger(
  trigger: ChatTrigger,
  currency: string,
  productTitles: ReadonlyMap<string, string> = new Map(),
): TriggerForm {
  return {
    name: trigger.name,
    kind: trigger.kind,
    enabled: trigger.enabled,
    oncePerVisitor: trigger.oncePerVisitor,
    rescueAfterSeconds:
      trigger.rescueAfterSeconds === null ? '' : String(trigger.rescueAfterSeconds),
    position: String(trigger.position),
    conditions: trigger.conditions.map((condition) => conditionToForm(condition, currency)),
    actions: trigger.actions.map((action) => actionToForm(action, productTitles)),
  };
}

function toCondition(form: ConditionForm, currency: string): ChatTriggerCondition {
  if (!isConditionField(form.field)) throw new Error(`Unknown condition field ${form.field}`);
  const kind = VALUE_KIND[form.field];
  const value = form.value.trim();
  // the form schema has checked field, op and value against each other
  switch (kind) {
    case 'text':
      return { field: form.field, op: form.op, value } as ChatTriggerCondition;
    case 'number':
      return { field: form.field, op: form.op, value: Number(value) } as ChatTriggerCondition;
    case 'money':
      return {
        field: 'cartTotalMinor',
        op: form.op,
        value: fromMajor(value, currency).amount,
      } as ChatTriggerCondition;
    case 'flag':
      return { field: form.field, op: 'is', value: value === 'true' } as ChatTriggerCondition;
    case 'countries':
      return {
        field: 'country',
        op: form.op,
        value: form.values.map((code) => code.toUpperCase()),
      } as ChatTriggerCondition;
    case 'attribute': {
      const numeric = /^-?\d+(?:\.\d+)?$/.test(value);
      return {
        field: 'viewingProductAttribute',
        key: form.key.trim(),
        op: form.op,
        value: numeric ? Number(value) : value,
      } as ChatTriggerCondition;
    }
    case 'list':
    case 'days':
    case 'devices':
      return { field: form.field, op: form.op, value: form.values } as ChatTriggerCondition;
  }
}

function toAction(form: ActionForm): CreateChatTriggerRequestActionsItem {
  switch (form.type) {
    case 'message':
      return {
        type: 'message',
        body: form.body.trim(),
        asUserId: form.sendAs === 'member' ? form.asUserId : null,
        alias: form.sendAs === 'alias' ? form.alias.trim() : null,
        suggestedReplies: form.suggestedReplies,
      };
    case 'wait':
      return { type: 'wait', seconds: Number(form.seconds) };
    case 'tag':
      return { type: 'tag', tag: form.tag.trim() };
    case 'product_card':
      return { type: 'product_card', productId: form.productId, variantId: null };
    case 'discount':
      return { type: 'discount', discountId: form.discountId };
  }
}

export function toCreateTrigger(form: TriggerForm, currency: string): CreateChatTriggerRequest {
  return {
    name: form.name.trim(),
    kind: form.kind,
    enabled: form.enabled,
    conditions: form.conditions.map((condition) => toCondition(condition, currency)),
    actions: form.actions.map(toAction),
    oncePerVisitor: form.oncePerVisitor,
    rescueAfterSeconds: form.kind === 'rescuer' ? Number(form.rescueAfterSeconds) : null,
    position: Number(form.position),
  };
}

/** Every field: the dialog edits the whole trigger. */
export function toUpdateTrigger(form: TriggerForm, currency: string): UpdateChatTriggerRequest {
  return toCreateTrigger(form, currency);
}
